import { DEFAULT_TIMEZONE } from "@dream/config/constants";
import { DateTime, IANAZone } from "luxon";
import { validationError } from "../errors.ts";
import type { Instant } from "../types.ts";

/**
 * Regras de tempo do Dream Calendar.
 *
 * Invariante: o banco guarda instantes absolutos (UTC) e, à parte, o IANA
 * timezone da regra. Toda conversão acontece aqui, nas bordas. Nenhum outro
 * módulo faz aritmética de fuso.
 */

const zoneCache = new Map<string, boolean>();

export function isValidTimezone(timezone: string): boolean {
  const cached = zoneCache.get(timezone);
  if (cached !== undefined) return cached;
  const valid = IANAZone.isValidZone(timezone);
  zoneCache.set(timezone, valid);
  return valid;
}

export function assertValidTimezone(timezone: string): string {
  if (!isValidTimezone(timezone)) {
    throw validationError(`Timezone inválido: "${timezone}"`, { timezone });
  }
  return timezone;
}

export function resolveTimezone(timezone: string | null | undefined): string {
  if (timezone === null || timezone === undefined || timezone.trim() === "") {
    return DEFAULT_TIMEZONE;
  }
  return assertValidTimezone(timezone.trim());
}

/** Converte um instante para o "wall clock" de um fuso. */
export function toZoned(instant: Instant, timezone: string): DateTime {
  return DateTime.fromJSDate(instant, { zone: assertValidTimezone(timezone) });
}

/**
 * Campos UTC de um `Date` como relógio de parede no fuso.
 *
 * Não é um instante absoluto. Com `timeZone` nomeado, o FullCalendar entrega
 * Dates cujos `getUTC*` são o horário local do calendário. `fromJSDate(date,
 * { zone })` aplicaria o offset outra vez: em America/Fortaleza a coluna da
 * segunda-feira (31/08) vira domingo.
 */
export function fromUtcWallClock(date: Date, timezone: string): DateTime {
  return DateTime.fromObject(
    {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
      hour: date.getUTCHours(),
      minute: date.getUTCMinutes(),
      second: date.getUTCSeconds(),
      millisecond: date.getUTCMilliseconds(),
    },
    { zone: assertValidTimezone(timezone) },
  );
}

/**
 * Interpreta componentes de hora local num fuso e devolve o instante absoluto.
 *
 * Horários inexistentes (salto de DST) e ambíguos (recuo de DST) são resolvidos
 * pelo Luxon de forma determinística: avança para o instante válido seguinte e,
 * na ambiguidade, escolhe o primeiro (offset anterior). O importante é que a
 * escolha seja estável e testada, não que seja arbitrária.
 */
export function fromLocalParts(
  parts: {
    year: number;
    month: number;
    day: number;
    hour?: number;
    minute?: number;
    second?: number;
  },
  timezone: string,
): Instant {
  const dt = DateTime.fromObject(
    {
      year: parts.year,
      month: parts.month,
      day: parts.day,
      hour: parts.hour ?? 0,
      minute: parts.minute ?? 0,
      second: parts.second ?? 0,
      millisecond: 0,
    },
    { zone: assertValidTimezone(timezone) },
  );

  if (!dt.isValid) {
    throw validationError(`Data/hora local inválida em ${timezone}: ${dt.invalidReason}`, {
      timezone,
      reason: dt.invalidExplanation,
    });
  }
  return dt.toJSDate();
}

/** Meia-noite local do dia de um instante, como instante absoluto. */
export function startOfLocalDay(instant: Instant, timezone: string): Instant {
  return toZoned(instant, timezone).startOf("day").toJSDate();
}

/** Instante imediatamente após o fim do dia local (fronteira exclusiva). */
export function endOfLocalDayExclusive(instant: Instant, timezone: string): Instant {
  return toZoned(instant, timezone).startOf("day").plus({ days: 1 }).toJSDate();
}

/**
 * Move um instante preservando a **hora local pretendida**, não o offset.
 *
 * É o núcleo da correção de drag-and-drop. Arrastar um evento de 09:00 para
 * outro dia deve manter 09:00 na parede, mesmo que os dois dias tenham offsets
 * UTC diferentes (transição de DST). Somar milissegundos ao instante quebraria
 * isso; reinterpretar os componentes locais não.
 */
export function shiftPreservingLocalTime(
  instant: Instant,
  delta: {
    days?: number;
    weeks?: number;
    months?: number;
    years?: number;
    minutes?: number;
  },
  timezone: string,
): Instant {
  return toZoned(instant, timezone).plus(delta).toJSDate();
}

/**
 * Reancora um instante em uma nova data local, preservando a hora local.
 * Usado quando o calendário informa a data destino de um arraste.
 */
export function moveToLocalDate(
  instant: Instant,
  target: { year: number; month: number; day: number },
  timezone: string,
): Instant {
  const zoned = toZoned(instant, timezone);
  return fromLocalParts(
    {
      year: target.year,
      month: target.month,
      day: target.day,
      hour: zoned.hour,
      minute: zoned.minute,
      second: zoned.second,
    },
    timezone,
  );
}

/** Duração em milissegundos entre dois instantes. */
export function durationMs(start: Instant, end: Instant): number {
  return end.getTime() - start.getTime();
}

/**
 * Aplica uma duração preservando o wall clock de destino.
 *
 * Para eventos com duração em dias, somar a duração absoluta atravessaria uma
 * transição de DST com uma hora de erro. Contamos dias no calendário local.
 */
export function applyDuration(
  start: Instant,
  reference: { start: Instant; end: Instant },
  timezone: string,
): Instant {
  const refStart = toZoned(reference.start, timezone);
  const refEnd = toZoned(reference.end, timezone);
  const wholeDays = Math.floor(refEnd.diff(refStart, "days").days);

  if (wholeDays >= 1) {
    const remainderMs = refEnd.diff(refStart.plus({ days: wholeDays })).toMillis();
    return toZoned(start, timezone).plus({ days: wholeDays, milliseconds: remainderMs }).toJSDate();
  }

  return new Date(start.getTime() + durationMs(reference.start, reference.end));
}

/**
 * Intervalo de dia inteiro para uma data local.
 *
 * Fim exclusivo, como no iCalendar e no FullCalendar: um evento de dia inteiro
 * em 15/03 vai de 15/03T00:00 local até 16/03T00:00 local.
 */
export function allDayRange(
  date: { year: number; month: number; day: number },
  timezone: string,
  days = 1,
): { startsAt: Instant; endsAt: Instant } {
  const startsAt = fromLocalParts(date, timezone);
  return {
    startsAt,
    endsAt: toZoned(startsAt, timezone)
      .plus({ days: Math.max(1, days) })
      .toJSDate(),
  };
}

/** Chave `YYYY-MM-DD` da data local. Usada para agrupar por dia sem ambiguidade. */
export function localDateKey(instant: Instant, timezone: string): string {
  return toZoned(instant, timezone).toFormat("yyyy-MM-dd");
}

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `YYYY-MM-DD` civil, sem hora. Null se o formato for outro. */
export function parseDateOnlyKey(
  value: string,
): { year: number; month: number; day: number } | null {
  const match = DATE_ONLY_RE.exec(value.trim());
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/**
 * Data de foco da URL do calendário.
 *
 * `YYYY-MM-DD` é data civil no fuso informado (não meia-noite UTC). ISO com
 * offset preserva o instante. Entrada vazia ou inválida cai em agora.
 */
export function parseFocusDate(raw: string | undefined, timezone: string): Instant {
  if (raw === undefined || raw.trim() === "") return new Date();
  const trimmed = raw.trim();
  const dateOnly = parseDateOnlyKey(trimmed);
  if (dateOnly !== null) {
    try {
      return fromLocalParts(dateOnly, timezone);
    } catch {
      return new Date();
    }
  }
  const dt = DateTime.fromISO(trimmed, { setZone: true });
  if (!dt.isValid) return new Date();
  return dt.toJSDate();
}

/** ISO 8601 com offset do fuso da entrega, para exibição e export. */
export function toZonedIso(instant: Instant, timezone: string): string {
  return toZoned(instant, timezone).toISO() ?? instant.toISOString();
}

/** Instantes que delimitam o mês local que contém a data informada. */
export function localMonthRange(
  instant: Instant,
  timezone: string,
): { start: Instant; end: Instant } {
  const zoned = toZoned(instant, timezone);
  return {
    start: zoned.startOf("month").toJSDate(),
    end: zoned.startOf("month").plus({ months: 1 }).toJSDate(),
  };
}

/** Semana local, iniciando no domingo (convenção do calendário brasileiro). */
export function localWeekRange(
  instant: Instant,
  timezone: string,
): { start: Instant; end: Instant } {
  const zoned = toZoned(instant, timezone);
  // Luxon usa ISO (segunda = 1). Recuamos até o domingo anterior.
  const sunday = zoned.startOf("day").minus({ days: zoned.weekday % 7 });
  return { start: sunday.toJSDate(), end: sunday.plus({ days: 7 }).toJSDate() };
}

export function rangesOverlap(
  a: { start: Instant; end: Instant },
  b: { start: Instant; end: Instant },
): boolean {
  // Semiaberto [start, end): eventos adjacentes não se sobrepõem.
  return a.start.getTime() < b.end.getTime() && b.start.getTime() < a.end.getTime();
}

export function parseInstant(value: string | Date): Instant {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) throw validationError("Data inválida");
    return value;
  }
  const dt = DateTime.fromISO(value, { setZone: true });
  if (!dt.isValid) {
    throw validationError(`Data/hora ISO 8601 inválida: "${value}"`, { value });
  }
  return dt.toUTC().toJSDate();
}

export { DEFAULT_TIMEZONE };
